<script>
    import {PATH_PREFIX} from '../../apiUrls.js';

	/*
		Time Confirmations Screen V1: SI-95
	 	Time Confirmations Screen V2: SWB-1852 (SW 2.8.0)
	*/

	import { onMount } from 'svelte';
	import { get } from '../../utils.js';
	import moment from 'moment';
	import {sgettext} from '../../i18n.js';

	export let striped = true;
	export let activityID = '';
	export let activityName = '';
	export let workOrderNumber = '';

	export let operations = [];

	// export let oldActivityOperationUrl = `${PATH_PREFIX}/sw/v2/api/operations/`;  // OperationViewSet
	export let activityOperationUrl = `${PATH_PREFIX}/sw/v2/api/sw_operations/`;  // SWOperationViewSet
	export let activityWorksequenceOperationsReportUrl = `${PATH_PREFIX}/sw/api/kendo/activity/{id}/worksequence_operations/report`;
	// export let operationSubmittedTimeUrl = `${PATH_PREFIX}/sw/v2/api/time_confirmations/`;  // TimeConfirmationStagingViewset (unused)
	// export let operationTimeConfirmed = `${PATH_PREFIX}/sw/v2/api/collated_time_confirmations/`;  // CollatedTimeConfirmationViewset (unused)
	// export let timeConfirmationsManualAdjustments = `${PATH_PREFIX}/sw/v2/api/time_confirmations_manual_adjustments/`;  // (unused)
	export let timeConfirmationsResponses = `${PATH_PREFIX}/sw/v2/api/time_confirmations_responses/`;
	export let userEmployeeIDUrl = `${PATH_PREFIX}/sw/api/ad_users/`;

	let SAPPlannedDurationTotal = 0;
	let SAPPlannedTimeTotal = 0;
	let SWPlannedTimeTotal = 0;
	let SWRecordedTimeTotal = 0;
	let SWUserAdjustmentsTotal = 0;
	let SWConfirmedTimeTotal = 0;
	let swMappedOperations = getSWMappedOperations(activityID);
	let operationData = getSAPOperationData(activityID);
	let resourceID = 0;
	let instanceTotals = {};
	let totalNotCommencedWorksequences = 0

	const SAP_STATUS = {
		SENT: sgettext('$$SENT$$'),
		PENDING: sgettext('$$PENDING$$'),
		FAILED: sgettext('$$FAILED$$'),
		NONE: ''
	};

	const TIME_CONFIRMATION_VERSIONS = {
		// Time Confirmation Versions map to Builder Release Numbers
		V1: "2.7.1",
		V2: "2.8.0"
	};

	const NOT_COMMENCED_WORKSEQUENCE_RESOURCE_NAME = sgettext('$$NOT_COMMENCED$$');

	// Static Images File Paths
	const STATIC_IMAGE_LOCATION = `${PATH_PREFIX}/sw/static/common/images/`;
	const SW_LOGO_LOCATION = STATIC_IMAGE_LOCATION + "sw.png";
	const SAP_LOGO_LOCATION = STATIC_IMAGE_LOCATION + "SAP.png";
	const SPACER_IMAGE_LOCATION = STATIC_IMAGE_LOCATION + "pixel.png";

	$: isCollapsedOperation = true;
	$: isCollapsedResource = true;

	// Instead of making lots of async network calls, this functions grabs all Operations, Work Sequences, Time Confirmations and Manual Adjustments and packages it into an Operations object
	// to be rendered throughout the application.
	async function getSAPOperationData(activityID) {
		// Fetch all Operations for a given Activity Version
        if (!operations.length) {
            const response = await get(
                `${activityOperationUrl}?activity_version=${activityID}`
            );
            const json = await response.json();
            if (response.ok) {
                if (json) {
                    operations = json.results;
                }
            }
        }

		// Fetch all Operations that have been mapped to a Work Sequence on the Operation Mapping page
		let mappedOperations = await swMappedOperations;
		let instanceExportedConfirmations = 0;
		let instanceImportedConfirmations = 0;
		let instanceFailedConfirmations = 0;
		let operationsData = [];
		for (let i = 0; i < operations.length; i++) {
			// Add SAP Planned Duration and Times to Instance Total Times
			SAPPlannedDurationTotal += operations[i].duration * 60;
			SAPPlannedTimeTotal +=
				operations[i].duration * 60 * operations[i].number_of_resources;

			let mappedOperation = operationKeySearch(
				operations[i].operation_number,
				mappedOperations
			);
			// Handle for when an Operation has no Work Sequences currently mapped
			if (mappedOperation === undefined) {
				mappedOperation = {
					work_sequences: '',
				};
			}

			// Fetch all User Submitted Time on the TimeConfirmationsStaging Table
			// let submittedTimes = await getSubmittedOperationTimes(operations[i].id);

			let submittedTimes = operations[i]['time_confirmation_staging_rows'];
			// let timeConfirmationStaging = {};

			// Try looking for time submitted under v1
			if (submittedTimes === undefined || submittedTimes.length === 0){
				submittedTimes = operations[i].time_confirmations;
			}
			let manualAdjustments = [];
			let operationRecordedTime = 0;
			for (let j = 0; j < submittedTimes.length; j++) {
				operationRecordedTime += Math.round(submittedTimes[j].fa_recorded_duration * 60);
				manualAdjustments.push({
					'workSequenceID': submittedTimes[j].work_sequence,
					'timeConfirmationStagingID': submittedTimes[j].id,
					'adjustment': submittedTimes[j].manual_adjustments
				});
			}

			SWRecordedTimeTotal += operationRecordedTime;

			// Determine Total User Adjusted Time for an Operation
			let operationUserAdjustments = 0;
			for (let j = 0; j < manualAdjustments.length; j++) {
				if(manualAdjustments[j].adjustment !== undefined){
					for(let l = 0; l < manualAdjustments[j].adjustment.length; l++){
						operationUserAdjustments += getAdjustedTime(
							manualAdjustments[j].adjustment[l].old_duration,
							manualAdjustments[j].adjustment[l].new_duration
						);
					}
				}
			}
			SWUserAdjustmentsTotal += Math.round(operationUserAdjustments);

			// Fetch all Final Time Confirmations
			// let confirmedTimes = await getOperationTimeConfirmed(operations[i].id);
			let confirmedTimes = operations[i].time_confirmations;
			let operationConfirmedTime = 0;
			let exportedConfirmations = 0;
			let importedConfirmations = 0;
			let failedConfirmations = 0;
			let operationSAPStatus = ''
			let hasVersionOneTimeConfirmations = false;

			for (let j = 0; j < confirmedTimes.length; j++) {
				operationConfirmedTime += Math.round(confirmedTimes[j].duration * 60);
				// Logic for determining if on the Operation Level all Time Confirmations have been sent to SAP.

				if (!hasVersionOneTimeConfirmations && confirmedTimes[j].version === TIME_CONFIRMATION_VERSIONS.V1)
					hasVersionOneTimeConfirmations = true;

				if (confirmedTimes[j].exported === true) {
					exportedConfirmations += 1;
					instanceExportedConfirmations += 1;
					if (confirmedTimes[j].imported_in_sap === false) {
						if(sapMessageHasError(confirmedTimes[j].sap_error_message)){
							failedConfirmations += 1
						}
					}
					else if (confirmedTimes[j].imported_in_sap === true) {
						importedConfirmations += 1;
						instanceImportedConfirmations += 1;
					}
				}
			}
			SWConfirmedTimeTotal += Math.round(operationConfirmedTime);

			if (hasVersionOneTimeConfirmations) {
				let confirmationsNotYetImportedInSAP = confirmedTimes.filter(x => x.duration > 0 && !x.imported_in_sap);
				if (confirmationsNotYetImportedInSAP.length) {
					operationSAPStatus = SAP_STATUS.PENDING;
				} else {
					operationSAPStatus = SAP_STATUS.SENT;
				}
			} else {
				// For status to be sent, all TimeConfirmations being sent to SAP should match the number confirmed as imported.
				if (importedConfirmations === exportedConfirmations && exportedConfirmations >= 1) {
					operationSAPStatus = SAP_STATUS.SENT;
					// Else there is still a TimeConfirmation in transit
				} else if (importedConfirmations !== exportedConfirmations && failedConfirmations >= 1) {
					operationSAPStatus = SAP_STATUS.FAILED;
				} else if (importedConfirmations !== exportedConfirmations && exportedConfirmations >= 1) {
					operationSAPStatus = SAP_STATUS.PENDING;
				} else {
					operationSAPStatus = SAP_STATUS.NONE;
				}
			}

			instanceFailedConfirmations += failedConfirmations

			let operationConfirmationNumber = undefined;

			for (let confirmedTime of confirmedTimes) {
				if (confirmedTime.sap_tc_response && confirmedTime.sap_tc_response['sap_confirmation_number']) {
					operationConfirmationNumber = confirmedTime.sap_tc_response['sap_confirmation_number'];
				}
			}

			// if (confirmedTimes.length > 0 && confirmedTimes[0].sap_tc_response) {
			//
			// 	operationConfirmationNumber = confirmedTimes[0].sap_tc_response['sap_confirmation_number'];
			// }

			// Check of an Operation has a description; Admin Operations currently don't
			let operationText = "";
			if(operations[i].operation_text){
				operationText = operations[i].operation_text;
			}
			// Take all Worksequences for the operation and tally the diff between scheduled end minus scheduled start
			let operationSWPlannedTime = getWorkSequencePlannedTime(mappedOperation.work_sequences)
			// Add the duration to the instance total
			SWPlannedTimeTotal += Math.round(operationSWPlannedTime)

			operationsData.push({
				operationObjectID: operations[i].id,
				operationNumber: operations[i].operation_number,
				operationText: operationText,
				roleCount: mappedOperation.role_count,
				operationSWPlannedTime: operationSWPlannedTime,
				operationSWMinimumPlannedTime: mappedOperation.minimum_duration_minutes,
				workSequences: mappedOperation.work_sequences,
				submittedTimes: submittedTimes,
				operationRecordedTime: operationRecordedTime,
				confirmedTimes: confirmedTimes,
				operationConfirmedTime: operationConfirmedTime,
				operationSAPStatus: operationSAPStatus,
				operationSAPFailedCount: failedConfirmations,
				manualAdjustments: manualAdjustments,
				operationUserAdjustments: operationUserAdjustments,
				operationConfirmationNumber: operationConfirmationNumber,
				hasVersionOneTimeConfirmations: hasVersionOneTimeConfirmations
			});
		}
		let instanceSAPStatus = '';
		if(instanceImportedConfirmations === instanceExportedConfirmations && instanceExportedConfirmations >= 1 ){
			instanceSAPStatus = SAP_STATUS.SENT;
		} else if (instanceImportedConfirmations !== instanceExportedConfirmations && instanceFailedConfirmations >= 1){
			instanceSAPStatus = SAP_STATUS.FAILED;
		} else if (instanceImportedConfirmations !== instanceExportedConfirmations && instanceExportedConfirmations >= 1){
			instanceSAPStatus = SAP_STATUS.PENDING;
		} else {
			instanceSAPStatus = SAP_STATUS.NONE;
		}

		instanceTotals = {
			sapPlannedDurationTotal: SAPPlannedDurationTotal,
			sapPlannedTimeTotal: SAPPlannedTimeTotal,
			swPlannedTimeTotal: SWPlannedTimeTotal,
			swRecordedTimeTotal: SWRecordedTimeTotal,
			swUserAdjustmentsTotal: SWUserAdjustmentsTotal,
			swConfirmedTimeTotal: SWConfirmedTimeTotal,
			sapStatus: instanceSAPStatus,
			sapFailedConfirmationsCount: instanceFailedConfirmations
		};

		return operationsData;
	}

	async function getSequenceData(operation) {
		let sequenceData = [];
		let timeConfirmations = [];
		let roles = [];
		let workSequences = [];
		let resourceConfirmations = [];
		let resourceSequences = [];
		let resourceRecordTimes = [];
		let resourceManualAdjustments = [];

		let unassignedWorkSequences = [];

		// Create an unique set of Roles for the Operation
		for (let i = 0; i < operation.workSequences.length; i++) {
			roles[i] = operation.workSequences[i].role_name;
			unassignedWorkSequences.push(operation.workSequences[i].id);
		}
		let uniqueRoles = [...new Set(roles)];

		//For each Role:
		for (let i = 0; i < uniqueRoles.length; i++) {
			let uniqueResources = [];
			uniqueResources = getUniqueResources(operation.submittedTimes);

			for (let j = 0; j < operation.workSequences.length; j++) {
				if (uniqueRoles[i] === operation.workSequences[j].role_name) {
					workSequences.push(operation.workSequences[j]);
					for(let l = 0; l < operation.submittedTimes.length; l++){
						if(operation.submittedTimes[l].work_sequence === operation.workSequences[j].id) {
							// Start removing known assigned Work Sequence ID's, leaving us with a list of unassigned Work Sequence ID's
							if(unassignedWorkSequences.length > 0){
								let index = unassignedWorkSequences.indexOf(operation.workSequences[j].id, 0);
								// indexOf will return -1 if it is not present
								if(index != -1){
									unassignedWorkSequences.splice(index, 1);
								}
							}
						}
					}
				}
			}

			if(unassignedWorkSequences.length > 0){
				// Add a "Unassigned" resource for all worksequences that are unassigned / have not had time logged
				let item = []
				item.push(NOT_COMMENCED_WORKSEQUENCE_RESOURCE_NAME)
				uniqueResources[NOT_COMMENCED_WORKSEQUENCE_RESOURCE_NAME] = item
			}
			totalNotCommencedWorksequences += unassignedWorkSequences.length
			// May not be unique - add extra checking
			// For each resource, find Submitted Time and Adjusted Time
			// uniqueResourcesEntries is a array of all dict entries in the uniqueResources object
			let uniqueResourcesEntries = Object.entries(uniqueResources)
			for (let j = 0; j < uniqueResourcesEntries.length; j++) {
				let submittedTimeIDs = [];
				for (let l = 0; l < operation.submittedTimes.length; l++) {
					for(let m = 0; m < workSequences.length; m++){
						if(workSequences[m].id === operation.submittedTimes[l].work_sequence){
							// If we have an employee ID, match on that otherwise match on user
							let userID = uniqueResourcesEntries[j][0];
							if (userID === operation.submittedTimes[l].employee_id || userID === operation.submittedTimes[l].user) {
								resourceRecordTimes.push(operation.submittedTimes[l]);
								submittedTimeIDs.push(operation.submittedTimes[l].id);
							}
						}
					}
				}
				// For each manualAdjustment object, loop through the adjustments and map adjustments to TimeComfStagingID
				for(let l = 0; l < operation.manualAdjustments.length; l++){
					if(operation.manualAdjustments[l].adjustment !== undefined){
						for(let m = 0; m < submittedTimeIDs.length; m++){
							if(submittedTimeIDs[m] === operation.manualAdjustments[l].timeConfirmationStagingID){
								for (let ma of operation.manualAdjustments[l].adjustment) {
									resourceManualAdjustments.push(ma);
								}
							}
						}
					}
				}
			}
			resourceManualAdjustments = [...new Set(resourceManualAdjustments)];

			// For each resource, find TimeConfirmations
			// Each Worksequence Array is at the Role Level, check to see if the Worksequence is in the list of sequences for the role
			for (let j = 0; j < uniqueResourcesEntries.length; j++) {

				let resourceUniqueID = uniqueResourcesEntries[j][0];
				let resourceAliases = uniqueResourcesEntries[j][1];

				let resourceSubmittedTimes = operation.submittedTimes.filter(s => {
					let tcUser = s.user;
					let tcEmployeeID = s.employee_id;

					return resourceUniqueID === tcUser || resourceUniqueID === tcEmployeeID || resourceAliases.includes(tcUser);

				});

				let resourceConfirmationIDs = resourceSubmittedTimes.map(s => s.time_confirmation);

				resourceConfirmations = [...resourceConfirmations, ...operation.confirmedTimes.filter(t => resourceConfirmationIDs.includes(t.id))];

			}




			for (let j = 0; j < resourceConfirmations.length; j++) {
				for (let l = 0; l < operation.workSequences.length; l++) {
					if (
						resourceConfirmations[j].work_sequence ===
						operation.workSequences[l].id
					) {
						resourceSequences.push(operation.workSequences[l]);
					}
				}
			}

			sequenceData.push({
				role: uniqueRoles[i],
				resources: uniqueResourcesEntries,
				roleSequences: workSequences,
				roleConfirmations: timeConfirmations,
				rolePlannedDuration: getWorkSequencePlannedTime(workSequences),
				roleRecordedTime: getRoleRecordedTime(resourceRecordTimes),
				roleUserAdjustedTime: getRoleUserAdjustedTime(resourceManualAdjustments),
				roleConfirmedTime: getRoleConfirmedTime(resourceRecordTimes),
				resourceSequences: resourceSequences,
				resourceConfirmations: resourceConfirmations,
				resourceRecordedTimes: resourceRecordTimes,
				resourceManualAdjustments: resourceManualAdjustments,
				unassignedWorkSequences: unassignedWorkSequences

			});
			// Reset variables per role
			timeConfirmations = [];
			workSequences = [];
			resourceSequences = [];
			resourceConfirmations = [];
			resourceRecordTimes = [];
			resourceManualAdjustments = [];
		}
		return sequenceData;
	}

	async function getResourceData(workSequenceData, userResourceEntry) {

		let resourceSequences = [];
		let resourceConfirmations = [];;
		let resourceWorkSequenceIDs = [];
		let resourceRecordedTimes = [];
		let resourceManualAdjustments = [];
		let resourceConfirmationResponseID = 0;
		let resourceSAPStatus = '';
		let activeDirectoryQueryResult = await getUsersName(userResourceEntry); //Get user details from AD
		let usersName = activeDirectoryQueryResult[0];
		let isEmployee = activeDirectoryQueryResult[1];

		let key = userResourceEntry[0]
		let userNameList = userResourceEntry[1]

		//For each Time Confirmation Staging, get each TimeConfirmationStaging and WorkSequenceID that matches the resource
		// Extra check on employeeID, A possible scenario is a contractor logs in with a different name but same contractor ID

		if(workSequenceData.resourceRecordedTimes.length > 0){
			for(let i = 0; i < workSequenceData.resourceRecordedTimes.length; i++){
				if(workSequenceData.resourceRecordedTimes[i].employee_id === key || workSequenceData.resourceRecordedTimes[i].user === key){
					resourceWorkSequenceIDs.push(workSequenceData.resourceRecordedTimes[i].work_sequence)

					// Temp/quick fix for 2.8.1.3 - a much better fix is available in 2.8.2 which will negate the need for this
					for (let j = 0; j < workSequenceData.resourceConfirmations.length; j++) {
						if (workSequenceData.resourceConfirmations[j].id === workSequenceData.resourceRecordedTimes[i].time_confirmation) {
							resourceConfirmations.push(workSequenceData.resourceConfirmations[j]);
						}
					}

				} else if (key === NOT_COMMENCED_WORKSEQUENCE_RESOURCE_NAME){
					resourceWorkSequenceIDs.push(workSequenceData.unassignedWorkSequences[i]);
				}
			}
		}
		if(key === NOT_COMMENCED_WORKSEQUENCE_RESOURCE_NAME){
			for(let i = 0; i < workSequenceData.unassignedWorkSequences.length; i++){
				resourceWorkSequenceIDs.push(workSequenceData.unassignedWorkSequences[i]);
			}
		}

		// //Get each Time Confirmation
		// for(let i = 0; i < workSequenceData.resourceConfirmations.length; i++){
		// 	if(workSequenceData.resourceConfirmations[i].employee_id === key || workSequenceData.resourceConfirmations[i].user === key || userNameList.includes(workSequenceData.resourceConfirmations[i].user)){
		// 		resourceConfirmations.push(workSequenceData.resourceConfirmations[i]);
		// 	}
		// }

		// Create a Unique Set of Work Sequence ID's as it's possible for multiple of the same WorkSequence ID from Time Confirmation Staging Rows
		resourceWorkSequenceIDs =   [...new Set(resourceWorkSequenceIDs)]
		//Find all WorkSequences that match the array of workSequence IDs for Confirmed Time
		for(let i  = 0; i < workSequenceData.roleSequences.length; i++){
			for(let j = 0; j < resourceWorkSequenceIDs.length; j++){
				if(workSequenceData.roleSequences[i].id === resourceWorkSequenceIDs[j]){
					resourceSequences.push(workSequenceData.roleSequences[i]);
				}
			}
		}

		// Find all RecordedTimes that match the array of workSequence IDs
		for(let i = 0; i < workSequenceData.resourceRecordedTimes.length; i++){
			for(let j = 0; j < resourceWorkSequenceIDs.length; j++){
				if(workSequenceData.resourceRecordedTimes[i].work_sequence === resourceWorkSequenceIDs[j]){
					if(workSequenceData.resourceRecordedTimes[i].employee_id === key || workSequenceData.resourceRecordedTimes[i].user === key){
						resourceRecordedTimes.push(workSequenceData.resourceRecordedTimes[i]);
					}
				}
			}
		}
		// Lazy fix - Rewrite and cleanup later
		resourceRecordedTimes = [...new Set(resourceRecordedTimes)];


		//Find all ManualAdjustments that match User Submitted Times
		for(let j = 0; j < resourceRecordedTimes.length; j++){
			for(let i = 0; i < workSequenceData.resourceManualAdjustments.length; i++){
				if(workSequenceData.resourceManualAdjustments[i] !== undefined){
					if(workSequenceData.resourceManualAdjustments[i].time_confirmation_staging === resourceRecordedTimes[j].id){
						resourceManualAdjustments.push(workSequenceData.resourceManualAdjustments[i]);
					}
				}
			}
		}

		let timeConfirmationCounter = '';
		for(let i = 0; i < resourceConfirmations.length; i++){
			if(resourceConfirmations[i].exported === true && resourceConfirmations[i].imported_in_sap === true){
				resourceSAPStatus = SAP_STATUS.SENT;
			} else if(resourceConfirmations[i].exported === true && resourceConfirmations[i].imported_in_sap === false && sapMessageHasError(resourceConfirmations[i].sap_error_message) === true) {
				resourceSAPStatus = SAP_STATUS.FAILED;
			} else if(resourceConfirmations[i].exported === true && resourceConfirmations[i].imported_in_sap === false) {
				resourceSAPStatus = SAP_STATUS.PENDING;
			} else {
				resourceSAPStatus = SAP_STATUS.NONE;
			}
		}

		resourceID += 1;

		let resourceData = {
			id: resourceID,
			usersName: usersName,
			usersNameList: userNameList,
			isEmployee: isEmployee,
			resourceSequences: resourceSequences,
			resourceConfirmations: resourceConfirmations,
			resourceRecordedTimes: resourceRecordedTimes,
			resourceManualAdjustments: resourceManualAdjustments,
			resourcePlannedTime: getWorkSequencePlannedTime(resourceSequences),
			resourceRecordedTime: getRoleRecordedTime(resourceRecordedTimes),
			resourceAdjustedRecordedTime: getRoleRecordedTime(resourceRecordedTimes),
			resourceManualAdjustedTime: getRoleUserAdjustedTime(resourceManualAdjustments),
			resourceConfirmedTime: await getResourceConfirmedTime(resourceConfirmations),
			resourceSAPStatus: resourceSAPStatus,
			timeConfirmationCounter: timeConfirmationCounter
		}
		return resourceData;
	}

// ***** API Functions *****
	async function getSWMappedOperations(activityID) {
		const response = await get(
			activityWorksequenceOperationsReportUrl.replace('{id}', activityID)
		);
		const json = await response.json();
		if (response.ok) {
			return json;
		}
	}

	async function getTimeConfirmationResponse(tcResponse, tcVersion) {
		let tcResponseID = tcResponse? tcResponse.id : null;

		let URL = `${timeConfirmationsResponses}?id=${tcResponseID}`;
		// Calls to TC Responses is not suported for Confirmations created prior to 2.8.0
		if (tcVersion === TIME_CONFIRMATION_VERSIONS.V2) {
			const response = await get(URL);
			const json = await response.json();
			if (response.ok) {
				if (json.count === 0) {
					return [];
				} else if (json) {
					return json.results;
				}
			} else {
				//console.error(json);
			}
		} else {
			return null;
		}
	}

	async function getUsersName(userResourceEntry) {
		// Three possible scenarios for the resource
		// 1: Has an userID of 2000 -> Query on EmployeeID
		// 2: Returns a length of 0, assume they are a contractor that exists in AD and perform a UPN lookup (Edge Case)
		// 3: Else return what is sent via FA

		let key = userResourceEntry[0]
		let user_details = null;

		const response = await get(
			userEmployeeIDUrl + '?employee_id=' + key
		);
		const json = await response.json();
		if (response.ok) {
			if (json.count >= 1) {
				user_details = json.results;
			} else {
				const response = await get(
					userEmployeeIDUrl + '?user_principal_name=' + key
				);
				const json = await response.json();
				if(response.ok){
					user_details = json.results;
				} else {
					//console.error(json)
				}
			}
		} else {
			//console.error(json);
		}

		let result = [key, false];  // [unique identifier, is employee?]

		try {
			if (user_details[0].given_name !== null) {
				let user = user_details[0];
				let name = `${user.given_name} ${user.surname}`;
				result = [name, true];
			}
		} catch (notFoundException) {

		}
		return result;
	}

// ***** Utility Functions *****
	function getAdjustedTime(oldTime, newTime) {
		if(oldTime !== undefined && newTime !== undefined){
			let duration = Math.round(newTime * 60) - Math.round(oldTime * 60);
			return Math.round(duration);
		}
		return 0;
	}

	function operationKeySearch(key, array) {
		for (let i = 0; i < array.length; i++) {
			if (array[i].sap_operation_number === key) {
				return array[i];
			}
		}
		return undefined;
	}

	function getSAPOperationTotals() {
		for (let i in operations) {
			SAPPlannedDurationTotal += operations[i].duration * 60;
			SAPPlannedTimeTotal +=
				operations[i].duration * 60 * operations[i].number_of_resources;
		}
	}

	function formatSAPCounter(sapCounter){
		return parseInt(sapCounter)
	}

	function getWorkSequencePlannedTime(workSequence) {
		let duration = 0;
		if(workSequence.length !== undefined){
			for (let i = 0; i < workSequence.length; i++) {
				// If the WorkSequence is a dummy WorkSequence from the Admin Operation, add no planned time.
				if(workSequence[i].id === null){
					return 0
				}
				let start = workSequence[i].scheduled_start_time;
				let end = workSequence[i].scheduled_end_time;
				let diff = moment(end, moment.ISO_8601).diff(
					moment(start, moment.ISO_8601)
				);
				// Doing a diff between Scheduled Start/End will account for seconds spent too
				// Parse as an int to round to the nearest number
				duration += Math.round(moment.duration(diff).asMinutes());
			}
		} else {
			if(workSequence.id === null){
				return "N/A"
			}
			let start = workSequence.scheduled_start_time;
			let end = workSequence.scheduled_end_time;
			let diff = moment(end, moment.ISO_8601).diff(
				moment(start, moment.ISO_8601)
			);
			duration =  Math.round(moment.duration(diff).asMinutes());
		}
		return duration;
	}

	function getRoleRecordedTime(timeConfirmationStagingRows){
		let duration = 0;
		for (let i = 0; i < timeConfirmationStagingRows.length; i++){
			duration += Math.round((timeConfirmationStagingRows[i].fa_recorded_duration) * 60);
		}
		return Math.round(duration)
	}

	function getRoleConfirmedTime(timeConfirmationRows){
		let duration = 0;
		for (let i = 0; i < timeConfirmationRows.length; i++){
			duration += Math.round((timeConfirmationRows[i].duration) * 60);
		}
		return Math.round(duration)
	}

	function getRoleUserAdjustedTime(manualAdjustments){
		let duration = 0;
		for(let i = 0; i < manualAdjustments.length; i++){
			if(manualAdjustments[i] !== undefined){
				duration += getAdjustedTime(
					manualAdjustments[i].old_duration,
					manualAdjustments[i].new_duration
				)
			}
		}
		return Math.round(duration)
	}

	async function getResourceConfirmedTime(resourceConfirmations) {

		let duration = 0;
		let resourceTimeConfirmations = [];
		if (resourceConfirmations === undefined || resourceConfirmations.length === 0) {
			resourceTimeConfirmations.push({
				'confirmationTime': 0,
				'confirmationIsFinal': false,
				'confirmationReason': '',
				'confirmationVersion': resourceConfirmations.version,
				exported: false,
				imported_in_sap: false
			});
		}

		let seenConfirmations = new Set();

		for (let i = 0; i < resourceConfirmations.length; i++) {
			if (seenConfirmations.has(resourceConfirmations[i].id)) {
				continue;
			} else {
				seenConfirmations.add(resourceConfirmations[i].id);
			}

			duration = Math.round(resourceConfirmations[i].duration * 60);
			resourceTimeConfirmations.push({
				id: resourceConfirmations[i].id,
				'confirmationTime': duration,
				'confirmationIsFinal': resourceConfirmations[i].is_final,
				'confirmationReason': sgettext(resourceConfirmations[i].partial_reason),
				'confirmationVersion': resourceConfirmations[i].version,
				work_sequence: resourceConfirmations[i].work_sequence,
				exported: resourceConfirmations[i].exported,
				imported_in_sap: resourceConfirmations[i].imported_in_sap,
				sap_error_message: resourceConfirmations[i].sap_error_message,
				sap_tc_response: await getTimeConfirmationResponse(resourceConfirmations[i].sap_tc_response, resourceConfirmations[i].version)
			})
		}

		return resourceTimeConfirmations
	}

	function getTotalResourceConfirmedTime(resourceConfirmedTime) {
		let duration = 0

		if (resourceConfirmedTime.length > 0) {
			let sum = resourceConfirmedTime.reduce((a, b) => ({confirmationTime: a.confirmationTime + b.confirmationTime}) );

			if (sum) {
				duration = Math.round(sum.confirmationTime);
			}
		}

		return duration;
	}

	async function getWorkSequenceTimes(workSequenceID, workSequenceData){
		let faRecordedTime = 0;
		let adjustedRecordedTime = 0;  // similar to fa_recorded_time but total is inclusive of all manual adjustments
		let manualAdjustedTimes = [];
		let confirmedTime = 0;
		let timeConfirmationStagingID = 0;
		let stagingRowIDs = [];

		for(let i = 0; i < workSequenceData.resourceRecordedTimes.length; i++){
			if(workSequenceData.resourceRecordedTimes[i].work_sequence === workSequenceID){
				stagingRowIDs.push(workSequenceData.resourceRecordedTimes[i].id);
				faRecordedTime += Math.round(workSequenceData.resourceRecordedTimes[i].fa_recorded_duration * 60);
				adjustedRecordedTime += Math.round(workSequenceData.resourceRecordedTimes[i].duration * 60);
				timeConfirmationStagingID = workSequenceData.resourceRecordedTimes[i].id;

				for(let i = 0; i < workSequenceData.resourceManualAdjustments.length; i++){
					if(workSequenceData.resourceManualAdjustments[i].time_confirmation_staging === timeConfirmationStagingID){
						manualAdjustedTimes.push({
							'id': workSequenceData.resourceManualAdjustments[i].id,
							'manualAdjustedTime': getAdjustedTime(
								workSequenceData.resourceManualAdjustments[i].old_duration,
								workSequenceData.resourceManualAdjustments[i].new_duration
							),
							'manualAdjustedReason': workSequenceData.resourceManualAdjustments[i].reason
						});
					}
				}
			}
		}

		if(workSequenceData.resourceManualAdjustments === undefined || workSequenceData.resourceManualAdjustments.length === 0){
			manualAdjustedTimes.push({
					'id': '0',
					'manualAdjustedTime': 0,
					'manualAdjustedReason': ''
			})
		}

		for(let i = 0; i < workSequenceData.resourceConfirmations.length; i++){
			if(workSequenceData.resourceConfirmations[i].work_sequence === workSequenceID){
				confirmedTime = Math.round(workSequenceData.resourceConfirmations[i].duration * 60)
			}
		}

		return {
			ids: stagingRowIDs,
			recordedTime: faRecordedTime,
			adjustedRecordedTime: adjustedRecordedTime,
			manualAdjustedTimes: manualAdjustedTimes,
			confirmedTime: confirmedTime
		}
	}

	function sapMessageHasError(sapMessage) {
		const SAP_EMPTY_MESSAGE = "{}"
		const SAP_SUCCESS_MESSAGE = "SUCCESS"

		// SAP Message could be null, in which case assume Time Comfirmation is still in transit
		if (sapMessage === undefined || sapMessage === null) {
			return false
			// Else we have a message, check to see if it's a success message
		} else if (sapMessage.includes(SAP_SUCCESS_MESSAGE) || sapMessage === SAP_EMPTY_MESSAGE) {
			return false
		}
		// Else it's an error message, return True
		return true
	}

	function getUniqueResources(stagingRows){
		let uniqueResources = {}
		let key = "";
		/*
		* For each staging row, create a unique list of resources
		* If a resource has an employee_id, group on that; else group on username
		*/
		for (let row of stagingRows) {
			if(row.employee_id){
				key = row.employee_id
			} else {
				key = row.user
			}

			// If they key already exists, i.e. 20001234: [Michael], push the new user's name to the list
			if (uniqueResources.hasOwnProperty(key)){
				let items = uniqueResources[key]
				items.push(row.user)
				// Start removing duplicate names from the list of names
				items = [...new Set(items)]
				uniqueResources[key] = items
			// Else if the key is not present, make a new item in the dict using that key
			} else {
				let list = [];
				list.push(row.user)
				uniqueResources[key] = list
			}
		}
		return uniqueResources
	}

	onMount(async () => {
		getSAPOperationTotals();
	});

	function expandCollapseOperation(id) {
		let target = window.$('.' + id).toggleClass('collapse');
	}

	function expandCollapseResource(id) {
		let target = window.$('.' + id).toggleClass('collapse');
	}

	function handleCollapsibleChildren(operationID) {
		/*
			Handles collapse logic for the given operation and all child rows of the operation.
		 */

		// :not(.multi-collapse-resource) is to avoid expanding individual resources
		let collapsibleChildren = jQuery(`.multi-collapse-operation-${operationID}:not(.multi-collapse-resource)`);
		let firstChild = collapsibleChildren.first();
		let firstChildIsVisible = firstChild.is(':visible');

		if (firstChildIsVisible) {
			// Removes :not(.multi-collapse-resource) because we want to collapse all children
			collapsibleChildren = jQuery(`.multi-collapse-operation-${operationID}`);
			collapsibleChildren.collapse('hide');
		}
		else {
			collapsibleChildren.collapse('show');
		}
	}

	function getDisplayName(resourceData) {
		/*
			Time confirmation data is sorted and grouped using a user's unique identifier. For employees, this is
			their email address.

			For contractors, this is their ERMS ID which is just a numerical string. But we don't want to
			display their records grouped under this ID even though this is actually what we do under the hood.

			We want to display their name instead.

			i.e.

			Employees: Show name as per details provided by Active Directory (data from FA is actually their email
					   address, we query AD with the email address to get their names).
			Contractors: Show name as entered on FA
		 */

		let uniqueIdentifier = resourceData.usersName; // for employees, this will be their name as per AD
		let userAliases = resourceData.usersNameList;  // for employees, this list will contain only their email
		let displayName = uniqueIdentifier;

		if (!resourceData.isEmployee) {
			displayName = userAliases.join(", ");
		}

		return displayName;
	}

</script>

<div class="">
	<div class="container-fluid">
		<h6 class="mb-1" id="section-general" sap-work-order-number={workOrderNumber}>{sgettext('$$ACTIVITY$$')}: {activityID}</h6>
		<h3 class="mb-4" data-tooltip={activityName}>
			{sgettext('$$TIME_CONFIRMATIONS$$')}: {activityName} {#if workOrderNumber}(W/O {workOrderNumber}){/if}
		</h3>
		{#await operationData}
			<h5>
				<b class="text-primary">{sgettext('$$FETCHING_DATA_STANDARDISED_WORK_PLEASE$$')} <i class="fa fa-spinner fa-spin"></i></b>
			</h5>
		{/await}
	</div>
</div>

<div class="section-general">
	<div class="container-fluid">
		<div class="tc-table row">
			<table class="mb-1" class:table-striped={striped} id="time-confirmations-summary-table">
				<thead>
					<tr>
						<td class="header"></td>
						<td colspan="3"  class="header">

						<!-- SAP banner -->
							<table border=0 cellspacing="0" cellpadding="0" width="100%" class="header">
								<tr class="header color-sap">
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
									<td class="header" rowspan="3" width="77"><img alt="Screenshot" src={SAP_LOGO_LOCATION} width="77" height="35"></td>
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
								</tr>
								<tr class="header color-sap">
									<td class="header img-header-sap"></td>
									<td class="header img-header-sap"></td>
								</tr>
								<tr class="header">
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
								</tr>
							</table>
						<!-- end SAP banner -->

						<td colspan="8" class="header">

							<!-- SW banner -->
							<table border=0 cellspacing="0" cellpadding="0" width="100%" class="header">
								<tr class="header">
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
									<td class="header" rowspan="3" width="74"><img alt="Screenshot" src={SW_LOGO_LOCATION} width="198" height="35"></td>
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
								</tr>
								<tr class="header">
									<td class="header td-num-roles img-header-sw"></td>
									<td class="header td-num-roles img-header-sw"></td>
								</tr>
								<tr class="header">
									<td class="header"><img width="1"  alt="pixel" src="{SPACER_IMAGE_LOCATION}"></td>
									<td class="header"><img width="1"  alt="pixel" src={SPACER_IMAGE_LOCATION}></td>
								</tr>
							</table>
						<!-- end SW banner -->

						</td>
					</tr>
					<tr>
						<th id="operation-th" class="text-left">{sgettext('$$OPERATION$$')}</th>
						<th class="td-text-right sap-th">{sgettext('$$ROLES$$')}</th>
						<th class="td-text-right th-planned sap-th">{sgettext('$$PLANNED_DURATION_MINS$$')}</th>
						<th class="td-text-right th-planned sap-th">{sgettext('$$PLANNED_TIME_MINS$$')}</th>
						<th class="td-text-right sw-th td-num-roles">{sgettext('$$ROLES$$')}</th>
						<th class="td-text-right sw-th">{sgettext('$$ROLE$$')}</th>
						<th class="td-text-right sw-th">{sgettext('$$RESOURCE$$')}</th>
						<th class="td-text-right th-planned sw-th">{sgettext('$$SW_PLANNED_TIME_MINS$$')}</th>
						<th class="td-text-right sw-th">{sgettext('$$RECORDED_TIME_MINS$$')}</th>
						<th class="td-text-right sw-th">{sgettext('$$USER_ADJUSTMENTS_MINS$$')}</th>
						<th class:fit={true} class="td-text-right th-confirmed-instance-total">{sgettext('$$CONFIRMED_TIME_MINS$$')}</th>
						<th class="td-text-right td-sap-status th-confirmed-instance-total sw-th">{sgettext('$$SAP_STATUS$$')}</th>
					</tr>
				</thead>
				<tbody>
					{#each operations as o, i}
						<tr id={o.id} class="parent">
							<td class="text-left expand operation-row bg-light-grey">
								{#await operationData then op}
									<a class="btn btn-link px-1 "
										on:click={() => {if (op[i].workSequences.length > 0) handleCollapsibleChildren(o.id)}}
										role="button"
										aria-expanded="true">
										<i class="fa fa-caret-down" class:invisible={op[i].workSequences.length === 0}></i>
										<b style="margin-left: 2px" class="text-left">{o.operation_number}</b>
									</a>
									<br />
									<p class:collapse={isCollapsedOperation}>
										{#if o.operation_text}
											{o.operation_text}
										{/if}
									</p>
								{/await}
							</td>
							<td class="td-text-right operation-row bg-light-grey">
								<b>{o.number_of_resources}</b>
							</td>
							<td class="td-sw-planned td-text-right operation-row">
								<b>{o.duration * 60}</b>
							</td>
							<td class="td-sw-planned td-text-right operation-row">
								<p class="font-weight-bolder">{o.number_of_resources * o.duration * 60}</p>
							</td>
							<td class="td-num-roles td-text-right operation-row bg-light-grey">
								{#await operationData}
									<b>...</b>
								{:then op}
									{#if op[i].roleCount !== undefined}
										<p class="font-weight-bolder">{op[i].roleCount}</p>
									{:else}
										<p></p>
									{/if}
								{/await}
							</td>
							<td class="operation-row bg-light-grey"></td>
							<td class="operation-row bg-light-grey"></td>
							<td class="td-sw-planned td-text-right operation-row">
								{#await operationData}
									<b>...</b>
								{:then op}
									{#if op[i].operationSWPlannedTime !== undefined}
										<p class="font-weight-bolder bg-operation-total">{op[i].operationSWPlannedTime}</p>
									{:else}
										<p></p>
									{/if}
								{/await}
							</td>
							<td class="td-text-right operation-row bg-light-grey">
								{#await operationData}
									<b>...</b>
								{:then op}
									{#if op[i] !== undefined}
										<p class="font-weight-bolder bg-operation-total">{op[i].operationRecordedTime}</p>
									{:else}
										<p></p>
									{/if}
								{/await}
							</td>
							<td class="td-text-right operation-row bg-light-grey">
								{#await operationData}
									<b>...</b>
								{:then op}
									{#if op[i] !== undefined}
										<p class="font-weight-bolder bg-operation-total">{op[i].operationUserAdjustments}</p>
									{:else}
										<p></p>
									{/if}
								{/await}
							</td>
							<td class="td-sw-confirmed td-text-right operation-row">
								{#await operationData}
									<b>...</b>
								{:then op}
									{#if op[i] !== undefined}
										<p class="font-weight-bolder bg-operation-total">{op[i].operationConfirmedTime}</p>
									{:else}
										<p></p>
									{/if}
								{/await}
							</td>
							<td class="td-sap-status td-sw-confirmed td-text-right operation-row">
								{#await operationData}
									<span>...</span>
								{:then op}
									{#if op[i].operationConfirmationNumber !== undefined}
										<strong>#{op[i].operationConfirmationNumber}</strong>
									{/if}
									{#if op[i].operationSAPStatus === SAP_STATUS.SENT}
										<span class="badge sap-status sap-status-sent">{SAP_STATUS.SENT}</span>
									{:else if op[i].operationSAPStatus === SAP_STATUS.FAILED}
										<span class="badge sap-status sap-status-failed"><b>{op[i].operationSAPFailedCount}</b> {sgettext('$$FAILED_CONFIRMATION$$')}</span>
									{:else if op[i].operationSAPStatus === SAP_STATUS.PENDING}
										{#if op[i].hasVersionOneTimeConfirmations}
											<i class="fa fa-info-circle text-light-grey" title={sgettext("$$OPERATION_TIME_CONFIRMED_PRIOR_RELEASE$$")}></i>
											<span class="badge sap-status sap-status-pending" title={sgettext("$$OPERATION_TIME_CONFIRMED_PRIOR_RELEASE$$")}>{SAP_STATUS.PENDING}</span>
										{:else}
											{#if op[i].operationConfirmationNumber !== undefined}
												<span class="badge sap-status sap-status-pending" title={`Confirmation #: ${op[i].operationConfirmationNumber}`}>
													{SAP_STATUS.PENDING}
												</span>
											{:else}
												<span class="badge sap-status sap-status-pending">{SAP_STATUS.PENDING}</span>
											{/if}
										{/if}
									{:else}
                                        <!--  -->
									{/if}
								{/await}
							</td>
						</tr>
						{#await operationData then op}
							{#await getSequenceData(op[i]) then sequenceData}
								{#each sequenceData as sd}
									<tr class="flex-wrapper child tr-role multi-collapse-operation multi-collapse-operation-{o.id} collapse">
										<td class="td-secondary td-operation-text bg-light-grey">{op[i].operationText}</td>
										<td class="td-secondary bg-light-grey"></td>
										<td class="td-sw-planned"></td>
										<td class="td-sw-planned"></td>
										<td class="td-num-roles td-secondary"></td>
										<td class="td-secondary td-role"><b>{sd.role}</b></td>
										<td class="td-secondary td-role"></td>
										<td class="td-sw-planned td-role td-text-right"><b>{sd.rolePlannedDuration}</b></td>
										<td class="td-secondary td-role td-text-right"><b>{sd.roleRecordedTime}</b></td>
										<td class="td-secondary td-role td-text-right"><b>{sd.roleUserAdjustedTime}</b></td>
										<td class="td-sw-confirmed td-role td-text-right"><b>{sd.roleConfirmedTime}</b></td>
										<td class="td-sw-confirmed td-role td-sap-status"></td>
									</tr>
									{#each sd.resources as r}
										{#await getResourceData(sd, r) then resourceData}
											{#if resourceData.resourceSequences.length}
											<tr class="flex-wrapper child multi-collapse-operation-{o.id} collapse">
												<td class="td-secondary bg-light-grey"></td>
												<td class="td-secondary bg-light-grey"></td>
												<td class="td-sw-planned"></td>
												<td class="td-sw-planned"></td>
												<td class="td-num-roles td-secondary bg-light-grey"></td>
												<td class="td-secondary bg-light-grey"></td>
												<td class="td-resource td-secondary">
													<a  class="btn btn-link px-1 href-resource-name"
														data-toggle="collapse"
														href={`.multi-collapse-resource-${resourceData.id}`}
														role="button"
														aria-expanded="true">
														<strong resource-unique-identifer={resourceData.usersName} style="white-space: nowrap;" class="text-left" title={`${resourceData.usersName} submitted time as:  ${resourceData.usersNameList}`}>
															<i class="fa fa-caret-down resource-caret"></i>
															{getDisplayName(resourceData)}
														</strong>
													</a>
												</td>
												<td class="td-sw-planned td-text-right"></td>
												<td class="td-secondary td-text-right"><b>{resourceData.resourceRecordedTime}</b></td>
												<td class="td-secondary td-text-right"><b>{resourceData.resourceManualAdjustedTime}</b></td>


												<td class="td-sw-confirmed td-text-right p-confirmed-time">
<!--													<b>{getTotalResourceConfirmedTime(resourceData.resourceConfirmedTime)}</b>-->

													{#each resourceData.resourceConfirmedTime as timeConfirmation}
														{#if timeConfirmation.confirmationVersion === TIME_CONFIRMATION_VERSIONS.V2}

															{#if timeConfirmation.confirmationIsFinal}
																{sgettext('$$FINAL$$')}
															{:else}
																{sgettext('$$PARTIAL$$')}: {sgettext(timeConfirmation.confirmationReason)}
															{/if}

															{timeConfirmation.confirmationTime}
															<br/>
<!--															<div class="d-flex">-->
<!--																<p class="p-manual-adjusted-reason td-text-right"><b></b></p>-->
<!--																{#if timeConfirmation.confirmationIsFinal == false}-->
<!--																	<p class="p-manual-adjusted-reason text-left">{sgettext('$$PARTIAL$$')}: {timeConfirmation.confirmationReason}</p>-->
<!--																{:else if timeConfirmation.confirmationIsFinal == true}-->
<!--																	<p class="p-manual-adjusted-reason text-left">{sgettext('$$FINAL$$')}</p>-->
<!--																{/if}-->
<!--															</div>-->
														{/if}
													{/each}

												</td>

												<td class="td-sap-status td-sw-confirmed td-text-right">
													{#each resourceData.resourceConfirmedTime as timeConfirmation}
														<div time-confirmation-id={timeConfirmation.id}>
															{#if timeConfirmation.confirmationVersion === TIME_CONFIRMATION_VERSIONS.V2 && timeConfirmation.exported === true && timeConfirmation.imported_in_sap === true}
																{#if timeConfirmation.sap_tc_response}
																	{sgettext('$$COUNTER$$')} #{formatSAPCounter(timeConfirmation.sap_tc_response[0].sap_counter)}
																{/if}

																<span class="badge sap-status sap-status-sent">
																	<b>{SAP_STATUS.SENT}</b>
																</span>
															{:else if timeConfirmation.confirmationVersion === TIME_CONFIRMATION_VERSIONS.V2 && timeConfirmation.exported && sapMessageHasError(timeConfirmation.sap_error_message)}
																<i class="fa fa-info-circle text-light-grey" title={timeConfirmation.sap_error_message}></i>
																<span class="badge sap-status sap-status-failed" title={timeConfirmation.sap_error_message}>{SAP_STATUS.FAILED}</span>
															{:else if timeConfirmation.confirmationVersion === TIME_CONFIRMATION_VERSIONS.V2 && timeConfirmation.exported}
																<span class="badge sap-status sap-status-pending">{SAP_STATUS.PENDING}</span>
																<p class="text-sm-right text-secondary">
																	{sgettext('$$AWAITING_RESPONSE_SAP$$')}
																</p>
															{:else if resourceData.resourceRecordedTimes.length > 0}
																{#if parseFloat(resourceData.resourceRecordedTimes[resourceData.resourceRecordedTimes.length - 1].duration) !== 0}
																	<span class="badge sap-status sap-status-pending">{SAP_STATUS.PENDING}</span>
																	<p class="text-sm-right text-secondary">
																			{sgettext(resourceData.resourceRecordedTimes[resourceData.resourceRecordedTimes.length - 1].status_of_last_export_attempt)}
																	</p>
																{/if}

															{/if}
														</div>
													{/each}
												</td>

											</tr>
											{/if}

											{#each resourceData.resourceSequences as rs}
												<tr class="flex-wrapper child multi-collapse-operation-{o.id} multi-collapse-operation multi-collapse-resource multi-collapse-resource-{resourceData.id} collapse" work-sequence-id={rs.id}>
													<td class="td-secondary bg-light-grey"></td>
													<td class="td-secondary bg-light-grey"></td>
													<td class="td-sw-planned"></td>
													<td class="td-sw-planned"></td>
													<td class="td-num-roles td-secondary bg-light-grey"></td>
													<td class="td-secondary bg-light-grey"></td>
													<td class="resource-row td-sequence">{rs.name}</td>
													<td class="td-sw-planned td-sequence td-text-right">{getWorkSequencePlannedTime(rs)}</td>
													{#await getWorkSequenceTimes(rs.id, resourceData) then wsTimes}
														<td class="resource-row td-sequence td-text-right" time-confirmation-staging-ids={wsTimes.ids}>{wsTimes.recordedTime}</td>
														<td class="resource-row td-sequence td-text-right td-manual-adjustment">
														{#each wsTimes.manualAdjustedTimes as manualAdjustedTime}
															{#if manualAdjustedTime.manualAdjustedTime !== 0}
																<span class="mr-2" manual-adjustment-id={manualAdjustedTime.id}>{manualAdjustedTime.manualAdjustedReason}</span>
																 {#if manualAdjustedTime.manualAdjustedTime > 0}+{/if}{manualAdjustedTime.manualAdjustedTime}
																<br/>
															{/if}
														{/each}
														</td>
														<td class="resource-row td-sequence td-sw-confirmed td-text-right" data-id={wsTimes.id}>{wsTimes.adjustedRecordedTime}</td>
													{/await}
													<td class="td-sw-confirmed td-text-right">

													</td>
												</tr>
											{/each}
										{/await}
									{/each}
								{/each}
							{/await}
						{/await}
					{/each}
					<tr class="tr-instance-total" id="instance-totals-row">
						<td class="td-instance-total"></td>
						<td class="td-instance-total"></td>
						<td class="td-instance-total td-text-right th-planned-instance-total">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.sapPlannedDurationTotal}</b>
							{/await}
						</td>
						<td class="td-instance-total td-text-right th-planned-instance-total">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.sapPlannedTimeTotal}</b>
							{/await}
						</td>
						<td class="td-num-roles td-instance-total"></td>
						<td class="td-instance-total"></td>
						<td class="td-instance-total"></td>
						<td class="td-instance-total td-text-right th-planned-instance-total ">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.swPlannedTimeTotal}</b>
							{/await}
						</td>
						<td class="td-instance-total td-text-right ">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.swRecordedTimeTotal}</b>
							{/await}
						</td>
						<td class="td-instance-total td-text-right">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.swUserAdjustmentsTotal}</b>
							{/await}
						</td>
						<td class="td-instance-total td-text-right th-confirmed-instance-total">
							{#await operationData}
                                <!--  -->
							{:then _}
								<b>{instanceTotals.swConfirmedTimeTotal}</b>
							{/await}
						</td>
						<td class="td-sap-status td-instance-total td-text-right th-confirmed-instance-total">
							{#await operationData}
                                <!--  -->
							{:then op}
								{#if instanceTotals.sapStatus === SAP_STATUS.SENT && totalNotCommencedWorksequences === 0}
									<span class="badge sap-status sap-status-sent">{SAP_STATUS.SENT}</span>
								{:else if instanceTotals.sapStatus === SAP_STATUS.FAILED}
									<span class="badge sap-status sap-status-failed">{instanceTotals.sapFailedConfirmationsCount} {sgettext("$$FAILED_CONFIRMATION$$")}</span>
								{:else if instanceTotals.sapStatus === SAP_STATUS.PENDING}
									<span class="badge sap-status sap-status-pending">{SAP_STATUS.PENDING}</span>
								{/if}
							{/await}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="d-flex justify-content-between table-footer mb-3">
				<div class="d-flex">
					<p class="p-planned">■ {sgettext('$$PLANNED$$')}   </p>
					<p class="p-confirmed">■ {sgettext('$$CONFIRMED$$')}   </p>
				</div>
				<div style="">
					<p class="">
						<i class="fa fa-info-circle"></i>
						{sgettext("$$PLEASE_NOTE_INDIVIDUAL_TIMES_DISPLAY_MAY$$")}
					</p>
				</div>
			</div>

		</div>
	</div>
</div>

<style>
	.section-general {
		margin-left: -5px;
	}
	table {
		width: 100%;
		overflow: hidden;
	}

	.fit {
		width: 1%;
		white-space: nowrap;
	}

	th {
		background-color: #dde2e6;
		color: black;
		padding: 16px;
	}

	/* Prevent the last instance total row from having highlighting by using it's ID */
	#instance-totals-row:hover td{
		pointer-events: none;
	}


	tbody tr:hover td:not(.td-instance-total) {
		background-color: #ffecc9;
	}

	tr:nth-child(even) {
		background-color: white;
	}

	tr:nth-child(odd) {
		background-color: white;
	}
	.tc-header {
		margin-left: 40px;
	}
	.align-right {
		text-align: right;
		align-items: flex-end;
	}

	.header {
		padding-top: 0px;
		padding-bottom: 0px;
		margin: 0px;
		height: 0.2px;
		background-color: #f2f4f5;
		color: #f2f4f5;
	}

	.th-planned {
		background-color: #0193b1;
		color: white;
	}

	.th-planned-instance-total {
		background-color: #016a8e !important;
		color: white;
		padding: 5px;
	}

	.th-confirmed {
		background-color: #0068b9;
		color: white;
	}
	.th-confirmed-instance-total {
		background-color: #1e5092 !important;
		color: white;
	}

	.header-td {
		width: 1%;
		background-color: #fff;
		border-color: #fff;
		border-top: 0px;
		border-bottom: 0px;
	}

	/*.operation-row{*/
	/*	background-color: #f0e8e8;*/
	/*}*/

	.resource-row {
		background-color: #eef4f8;
	}

	.resource-row-planned{
		background-color: #dee7ec
	}

	.resource-row-confirmed{
		background-color: #dfe4e8
	}

	.td-sw-planned {
		background-color: #e1edf1;
	}

	.td-sw-confirmed {
		background-color: #dfe4e8
	}

	#instance-totals-row td {
		background-color: #dde2e6;
	}

	.td-instance-total {
		background-color: #dde2e6;
	}

	.td-secondary {
		background-color: #f7f9fa;
	}

	.td-secondary {
		/*background-color: #fdfdfe;*/
		background-color: #eef4f8;
	}

	.td-resource{
		padding-left: 0px
	}

	.td-sequence {
		color: #878787;
		/*background-color: #f4f8ff;*/
		font-size: 0.8rem;
		padding: 5px;
	}

	.bhp-primary {
		color: #dd5900;
	}

	.td-operation-text {
		padding-left: 20px;
	}

	.tc-table {
		padding-left: 25px;
		padding-right: 25px;
	}

	.p-planned {
		color: #0094b2;
		margin-left: 5px;
		margin-right: 5px;
	}

	.p-confirmed {
		color: #0068b9;
		margin-left: 5px;
		margin-right: 5px;
	}

	.p-sap{
		color: #2260b6;
		margin-left: 5px;
		margin-right: 5px;
	}

	.p-sw{
		color: #dd5900;
		margin-left: 5px;
		margin-right: 5px;
	}

	.p-confirmed-time{
		margin-left: 5px;
	}

	.p-confirmed-status{
		margin-top: 2px;
		margin-bottom: 2px;
	}

	.p-manual-adjusted-reason{
		font-size: 15px;
		margin-right: 10px;
		margin-top: 0px;
		margin-bottom: 0px;
		flex: 1;
		/*text-align: right;*/
	}

	.p-manual-adjusted-reason:nth-of-type(even) {
		flex: 2;
		/*text-align: left;*/
	}

	.sap-status {
		box-sizing: border-box;
		border-radius: 6px;
		padding: 4px 10px;
		margin: 3px 3px;
	}

	.sap-status-sent {
		color: white;
		background-color: #25a745;
	}

	.sap-status-pending {
		color: white;
		background-color: #059cdf;
	}

	.sap-status-failed {
		color: white;
		background-color: #df0505;
	}

	.expand {
		color: #a64b0d;
		min-width: 150px;
	}

	.sap_has_failed {
		color: red;
	}
	:global([data-toggle='collapse'].collapsed i) {
		transform: rotate(-90deg);
	}

	.collapse .parent i {
		transform: rotate(-90deg);
	}

	.table-footer {
		width: 100%;
	}

	.parent {
		border-top: 1px solid #ced5dd;
		border-bottom: none;
		border-left: 1px solid #ced5dd;
		border-right: 1px solid #ced5dd;
	}

	.child {
		border-top: none;
		border-bottom: none;
		border-left: 1px solid #ced5dd;
		border-right: 1px solid #ced5dd;
	}

	.bg-sequence {
		background-color: #f4f8ff;
	}

	.bg-light-grey {
		background-color: #eef4f8;
	}

	.bg-lightest-grey {
		background-color: #f4f8ff;
	}

	.img-header-sw {
		border-bottom: 5px solid #DD5900;
	}

	.img-header-sap {
		border-bottom: 5px solid #2260B6;
	}

	.font-weight-bolder {
		font-weight: 800;
		margin-bottom: 0;
	}

	.bg-operation-total {
		/*background-color: white;*/
		/*border-radius: 2px;*/
	}

	.text-light-grey {
		color: #a1a9b1;
	}

	.td-role {
		border-top: 0.5px solid #ced5dd;
		padding-right: 15px;
	}

	.td-text-right {
		text-align: right;
		padding-right: 15px;
	}

	.href-resource-name:hover {
		text-decoration: none;
	}

	.td-manual-adjustment {
        white-space: normal;
        text-align: left;
	}

	.td-num-roles {
		border-left: 4px solid white;
	}

	.td-sap-status {
		border-left: 1px solid white;
	}
</style>
